/**
 * SpendPulseChart — 7-day spend area chart (Administrator-only).
 *
 * Contract (PRD §5.7):
 *   Props: { series: CostSpendPoint[] }
 *   - series.length === 0 -> "No spend data yet." placeholder
 *   - all-zero series -> chart with "$0 across this period" caption
 *   - otherwise -> Recharts area chart
 *   NEVER throws. Wrapped in <ConvexErrorBoundary> by parent.
 */
import '../../../styles/screens/today.css';
import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import type { CostSpendPoint } from "../../../../../../shared/types/operatorHome";
import { useT } from "../../../i18n/useT";

interface SpendPulseChartProps {
  series: CostSpendPoint[];
}

function formatDate(ts: number): string {
  const t = useT();

  try {
    return new Date(ts).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function SpendPulseChart({
  series,
}: SpendPulseChartProps): React.ReactElement {
  // Empty series placeholder
  if (series.length === 0) {
    return (
      <div data-testid="spend-pulse-chart">
        <p style={{ color: "var(--text-tertiary, #94a3b8)", fontSize: "0.875rem" }}>
          No spend data yet.
        </p>
      </div>
    );
  }

  const allZero = series.every((point) => point.amountUsd === 0);

  const chartData = series.map((point) => ({
    date: formatDate(point.ts),
    amount: point.amountUsd,
  }));

  return (
    <div data-testid="spend-pulse-chart">
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: "var(--text-tertiary, #94a3b8)" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "var(--text-tertiary, #94a3b8)" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => `$${v}`}
            width={40}
          />
          <Tooltip
            formatter={(value: number) => [`$${value.toFixed(2)}`, t("today.spend.label")]}
            labelFormatter={(label: string) => label}
          />
          <Area
            type="monotone"
            dataKey="amount"
            stroke="var(--color-sora, #6366f1)"
            fill="var(--color-sora-light, rgba(99, 102, 241, 0.1))"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
      {allZero && (
        <p
          style={{
            fontSize: "0.75rem",
            color: "var(--text-tertiary, #94a3b8)",
            textAlign: "center",
            marginTop: "4px",
          }}
        >
          $0 across this period
        </p>
      )}
    </div>
  );
}
